import React, { useEffect, useId, useRef } from 'react';
import { createPortal }                  from 'react-dom';
import { MdClose }                       from 'react-icons/md';

/* Dialogs currently open, oldest first */
const openDialogs = [];

/**
 * Modal dialog for Chronos edit screens: a backdrop, a box centred in the
 * visible window with its own scrolling, a header with the title and a close
 * cross, the content and an optional footer for actions.
 *
 * It is rendered into document.body rather than in place, so the online
 * diary's "main button" / "main dl" styles don't reach it; it is styled by the
 * .chronos-dialog rules in styles.css instead.
 *
 * Closes on the cross or Escape. A click on the backdrop does not close it, so
 * a stray click can't lose unsaved changes. Focus moves into the dialog when
 * it opens and back to where it was when it closes.
 * @param {string}   title
 * @param {function} onClose
 * @param {node}     footer    optional actions, shown below the content
 * @param {string}   className optional extra class for the dialog box
 * @param {string}   role      'dialog', or 'alertdialog' for a confirmation
 */
const Dialog = ({ title, onClose, footer = null, className = '', role = 'dialog', children }) => {

    const titleID = useId();
    const dialogRef = useRef(null);

    // A ref, so a new onClose each render doesn't rerun the effect and steal focus
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        const previousFocus = document.activeElement;
        const self = {};
        openDialogs.push(self);
        dialogRef.current?.focus();

        // Only the top dialog closes, e.g. a confirmation over an editor
        const handleKeyDown = e => {
            if ('Escape' === e.key && openDialogs[openDialogs.length - 1] === self) {
                e.stopPropagation();
                onCloseRef.current();
            }
        };
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            openDialogs.splice(openDialogs.indexOf(self), 1);
            previousFocus?.focus?.();
        };
    }, []);

    return createPortal(
        <div className='chronos-dialog-backdrop'>
            <section ref={dialogRef} className={'chronos-dialog ' + className}
                role={role} aria-modal='true' aria-labelledby={titleID} tabIndex={-1} >
                <header className='chronos-dialog__header'>
                    <h2 id={titleID} className='chronos-dialog__title'>{title}</h2>
                    <button type='button' className='chronos-icon-button'
                        aria-label='Close' title='Close' onClick={onClose} >
                        <MdClose aria-hidden='true' />
                    </button>
                </header>
                <div className='chronos-dialog__body'>
                    {children}
                </div>
                {footer && <footer className='chronos-dialog__footer'>{footer}</footer>}
            </section>
        </div>,
        document.body
    );
};

export default Dialog;
